import React from "react";

/**
 * Natalio — asistente 360 de All Active.
 * SVG robot con micro-animaciones CSS puras:
 *  - "idle": flota levemente y parpadea
 *  - "wave": saluda con la mano (mensaje inicial)
 *  - "thinking": la antena parpadea rápido y la boca se ondula
 *
 * Colores: brand orange (#FF5A00) + gris frío. Se adapta al tamaño con `size` en px.
 */
export default function Natalio({ size = 44, mood = "idle", className = "" }) {
  return (
    <div
      className={`natalio natalio--${mood} ${className}`}
      style={{ width: size, height: size }}
      aria-hidden="true"
      data-testid="natalio-avatar"
    >
      <svg viewBox="0 0 64 64" width={size} height={size} className="natalio__svg">
        {/* Antena */}
        <line x1="32" y1="8" x2="32" y2="4" stroke="#0A0A0A" strokeWidth="2" strokeLinecap="round" />
        <circle className="natalio__antenna-dot" cx="32" cy="4" r="2.5" fill="#FF5A00" />

        {/* Cuerpo (cabeza redondeada) */}
        <rect x="12" y="10" width="40" height="34" rx="10" fill="#F4F4F4" stroke="#0A0A0A" strokeWidth="2" />

        {/* Reflejo/brillo */}
        <path d="M18 16 Q22 12 30 12" stroke="white" strokeWidth="2" fill="none" strokeLinecap="round" opacity="0.9" />

        {/* Ojos */}
        <g className="natalio__eyes">
          <circle className="natalio__eye" cx="24" cy="26" r="4" fill="#0A0A0A" />
          <circle className="natalio__eye" cx="40" cy="26" r="4" fill="#0A0A0A" />
          {/* brillito ojo */}
          <circle cx="25.5" cy="24.5" r="1" fill="white" />
          <circle cx="41.5" cy="24.5" r="1" fill="white" />
        </g>

        {/* Boca / display */}
        <rect className="natalio__mouth" x="24" y="34" width="16" height="4" rx="2" fill="#FF5A00" />

        {/* Cuello */}
        <rect x="28" y="44" width="8" height="4" fill="#0A0A0A" />

        {/* Cuerpo bajo (torso pequeño) */}
        <rect x="16" y="48" width="32" height="10" rx="4" fill="#0A0A0A" />
        {/* Botones */}
        <circle cx="24" cy="53" r="1.5" fill="#FF5A00" />
        <circle cx="32" cy="53" r="1.5" fill="#F4F4F4" />
        <circle cx="40" cy="53" r="1.5" fill="#F4F4F4" />

        {/* Brazo saluda (solo en mood wave) */}
        {mood === "wave" && (
          <g className="natalio__wave-arm">
            <line x1="52" y1="30" x2="60" y2="22" stroke="#0A0A0A" strokeWidth="3" strokeLinecap="round" />
            <circle cx="60" cy="22" r="3" fill="#F4F4F4" stroke="#0A0A0A" strokeWidth="2" />
          </g>
        )}
      </svg>

      <style>{`
        .natalio {
          display: inline-block;
          transform-origin: center bottom;
        }
        .natalio__svg { display: block; }

        /* Idle: flota suave y parpadea */
        .natalio--idle .natalio__svg { animation: natalio-float 3.2s ease-in-out infinite; }
        .natalio--idle .natalio__eyes { animation: natalio-blink 4.6s infinite; transform-origin: center; }
        .natalio--idle .natalio__antenna-dot { animation: natalio-pulse 2.4s ease-in-out infinite; }
        .natalio--idle .natalio__mouth { animation: natalio-mouth-idle 5s ease-in-out infinite; transform-origin: 32px 36px; }

        /* Wave: saluda + energía extra */
        .natalio--wave .natalio__svg { animation: natalio-bounce 1s ease-in-out infinite; }
        .natalio--wave .natalio__wave-arm {
          transform-origin: 52px 30px;
          animation: natalio-wave 0.7s ease-in-out infinite;
        }
        .natalio--wave .natalio__antenna-dot { animation: natalio-pulse 1s ease-in-out infinite; }

        /* Thinking: parpadeo rápido en la antena + boca ondulante */
        .natalio--thinking .natalio__svg { animation: natalio-float 1.6s ease-in-out infinite; }
        .natalio--thinking .natalio__antenna-dot {
          animation: natalio-blink-fast 0.55s infinite;
          fill: #FF5A00;
        }
        .natalio--thinking .natalio__mouth {
          animation: natalio-mouth-think 1.1s ease-in-out infinite;
          transform-origin: 32px 36px;
        }

        @keyframes natalio-float {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-3px); }
        }
        @keyframes natalio-bounce {
          0%, 100% { transform: translateY(0) rotate(-2deg); }
          50% { transform: translateY(-4px) rotate(2deg); }
        }
        @keyframes natalio-blink {
          0%, 92%, 100% { transform: scaleY(1); }
          94%, 98% { transform: scaleY(0.1); }
        }
        @keyframes natalio-blink-fast {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.25; }
        }
        @keyframes natalio-pulse {
          0%, 100% { opacity: 1; r: 2.5; }
          50% { opacity: 0.6; r: 3.2; }
        }
        @keyframes natalio-wave {
          0%, 100% { transform: rotate(0deg); }
          50% { transform: rotate(-25deg); }
        }
        @keyframes natalio-mouth-idle {
          0%, 100% { transform: scaleX(1); }
          50% { transform: scaleX(0.85); }
        }
        @keyframes natalio-mouth-think {
          0%, 100% { transform: scaleX(0.4) scaleY(1); }
          50% { transform: scaleX(1) scaleY(1.4); }
        }

        @media (prefers-reduced-motion: reduce) {
          .natalio__svg, .natalio__eyes, .natalio__antenna-dot,
          .natalio__mouth, .natalio__wave-arm { animation: none !important; }
        }
      `}</style>
    </div>
  );
}
